import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";

export default function ViabilityPane() {
  const [licitaciones, setLicitaciones] = useState([]);
  const [selected, setSelected] = useState("");
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [history, setHistory] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [ctxPreview, setCtxPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/licitaciones`)
      .then(r => setLicitaciones(r.data || []))
      .catch(() => {});
  }, []);

  const loadDecisions = (lid) => {
    axios.get(`${API}/tender360/viability/${lid}/decisions`)
      .then(r => setDecisions(r.data || []))
      .catch(() => setDecisions([]));
  };

  useEffect(() => {
    if (!selected) { setHistory([]); setResult(null); setResultId(null); setDecisions([]); setCtxPreview(null); return; }
    axios.get(`${API}/tender360/viability/${selected}/history`)
      .then(r => {
        setHistory(r.data || []);
        if (r.data?.[0]) { setResult(r.data[0].analysis); setResultId(r.data[0].id); }
      })
      .catch(() => {});
    setCtxPreview(null);
    axios.get(`${API}/tender360/viability/${selected}/context-preview`)
      .then(r => setCtxPreview(r.data)).catch(() => {});
    loadDecisions(selected);
  }, [selected]);

  const runViability = async () => {
    if (!selected) { toast.error("Selecciona una licitación"); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await postAiJob(`${API}/tender360/viability/${selected}`, {
        additional_context: extra,
      });
      setResult(res);
      setResultId(res.id);
      toast.success("Análisis de viabilidad completado");
      const h = await axios.get(`${API}/tender360/viability/${selected}/history`);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al generar análisis");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="viability-pane">
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Gauge size={16} className="text-[#007934]"/> Go / No-Go automático</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Licitación</label>
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              data-testid="viability-licitacion-select"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white"
            >
              <option value="">— Elegir —</option>
              {licitaciones.map(l => {
                const label = `${l.municipio || "?"} · ${(l.titulo || "").slice(0, 80)}`;
                return <option key={l.id} value={l.id}>{label}</option>;
              })}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={runViability}
              disabled={loading || !selected}
              data-testid="viability-run-btn"
              className="w-full bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
              {loading ? "Analizando..." : "Lanzar Go/No-Go Pando"}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Contexto adicional (opcional)</label>
          <textarea
            value={extra}
            onChange={e => setExtra(e.target.value)}
            placeholder="Ej: tenemos referencias en Madrid Este, Urbaser es incumbente..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white resize-y"
            rows={2}
          />
        </div>
        {/* V07 · Previsualización del contexto que usará el Go/No-Go (antes de lanzar) */}
        {selected && ctxPreview && (
          <div className="border border-sky-200 bg-sky-50/70 rounded-lg p-3" data-testid="viability-context-preview">
            <div className="text-[10px] uppercase font-semibold text-sky-700 tracking-wide mb-1.5">Contexto que usará el análisis</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {ctxPreview.fuentes.map((f, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]" data-testid={`viability-ctx-src-${i}`}>
                  <span className={f.present ? "text-emerald-600" : "text-amber-600"}>{f.present ? "✓" : "⚠"}</span>
                  <span className="text-gray-700"><b>{f.label}:</b> <span className="text-gray-500">{f.detail}</span></span>
                </div>
              ))}
            </div>
            {ctxPreview.faltan?.length > 0 && (
              <p className="text-[10px] text-amber-700 mt-1.5">Faltan: {ctxPreview.faltan.join(", ")} — reducen la fiabilidad del score.</p>
            )}
            <p className="text-[10px] text-sky-700 mt-1">{ctxPreview.nota}</p>
          </div>
        )}
      </div>

      {result && <ViabilityResult data={result} />}

      {/* Iter309 · C02 — decisión humana trazable sobre la propuesta de la IA */}
      {selected && <GoNoGoDecisionPanel lid={selected} resultId={resultId} result={result} decisions={decisions} onDecided={() => loadDecisions(selected)} />}

      {history.length > 1 && (
        <details className="bg-white rounded-xl border border-gray-200 p-3">
          <summary className="cursor-pointer text-xs font-semibold text-gray-600">Histórico de análisis ({history.length})</summary>
          <div className="mt-2 space-y-1">
            {history.map(h => (
              <div key={h.id} className="text-[11px] p-2 border border-gray-100 rounded flex items-center justify-between">
                <div>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold mr-2
                    ${h.analysis.decision === "GO" ? "bg-green-100 text-green-700" :
                      h.analysis.decision === "NO_GO" ? "bg-rose-100 text-rose-700" :
                      "bg-amber-100 text-amber-700"}`}>
                    {h.analysis.decision}
                  </span>
                  <span className="text-gray-600">{h.analysis.score_total}/100</span>
                </div>
                <span className="text-gray-400">{new Date(h.generated_at).toLocaleString("es-ES")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter309 · C02 — Decisión humana trazable. El scoring de la IA propone;
// una persona autorizada decide GO / NO-GO / GO condicional dejando motivo
// obligatorio. Nunca sobrescribe decisiones previas — queda el histórico
// completo (auditoría).
// ═══════════════════════════════════════════════════════════════════════════
const DECISION_META = {
  GO:              { label: "GO",              cls: "bg-green-500 text-white",  btnCls: "border-green-600 text-green-700 hover:bg-green-50" },
  NO_GO:           { label: "NO-GO",           cls: "bg-rose-500 text-white",   btnCls: "border-rose-600 text-rose-700 hover:bg-rose-50" },
  GO_CONDICIONAL:  { label: "GO condicional",  cls: "bg-amber-500 text-white",  btnCls: "border-amber-600 text-amber-700 hover:bg-amber-50" },
};

function GoNoGoDecisionPanel({ lid, resultId, result, decisions, onDecided }) {
  const [picking, setPicking] = useState(null); // "GO" | "NO_GO" | "GO_CONDICIONAL" | null
  const [motivo, setMotivo] = useState("");
  const [condicionesAceptadas, setCondicionesAceptadas] = useState([]);
  const [saving, setSaving] = useState(false);

  const latest = decisions?.[0] || null;
  const condicionesIA = result?.condiciones || [];

  const toggleCondicion = (c) => setCondicionesAceptadas(prev =>
    prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
  );

  const confirmDecision = async () => {
    if (motivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tender360/viability/${lid}/decision`, {
        decision: picking,
        motivo: motivo.trim(),
        viability_analysis_id: resultId,
        condiciones_aceptadas: picking === "GO_CONDICIONAL" ? condicionesAceptadas : [],
      });
      toast.success("Decisión registrada y auditada");
      setPicking(null); setMotivo(""); setCondicionesAceptadas([]);
      onDecided?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "No se pudo registrar la decisión");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="gonogo-decision-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ClipboardCheck size={16} className="text-[#007934]"/> Decisión humana (trazable)
        </h3>
        {latest && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${DECISION_META[latest.decision]?.cls || "bg-gray-400 text-white"}`}
            data-testid="gonogo-latest-decision"
            title={`Decidido por ${latest.decided_by} el ${new Date(latest.decided_at).toLocaleString("es-ES")}`}>
            {DECISION_META[latest.decision]?.label || latest.decision}
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-500">
        El score de arriba es una propuesta de la IA. La decisión final — y su responsabilidad — es de una persona autorizada, con motivo obligatorio.
      </p>

      {latest && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-xs" data-testid="gonogo-latest-box">
          <p className="text-gray-700"><b>{latest.decided_by}</b> · {new Date(latest.decided_at).toLocaleString("es-ES")}</p>
          <p className="text-gray-600 mt-1">{latest.motivo}</p>
          {latest.condiciones_aceptadas?.length > 0 && (
            <ul className="mt-1.5 list-disc pl-4 text-amber-800">
              {latest.condiciones_aceptadas.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          )}
        </div>
      )}

      {!picking ? (
        <div className="flex flex-wrap gap-2">
          {Object.entries(DECISION_META).map(([key, meta]) => (
            <button
              key={key}
              onClick={() => { setPicking(key); setMotivo(""); setCondicionesAceptadas([]); }}
              data-testid={`gonogo-pick-${key.toLowerCase()}`}
              className={`text-xs font-semibold border rounded-lg px-3 py-2 transition-colors ${meta.btnCls}`}>
              {meta.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-3 space-y-2.5" data-testid="gonogo-decision-form">
          <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${DECISION_META[picking].cls}`}>
            {DECISION_META[picking].label}
          </div>
          {picking === "GO_CONDICIONAL" && condicionesIA.length > 0 && (
            <div>
              <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Condiciones que se exigen cumplir</label>
              <div className="space-y-1">
                {condicionesIA.map((c, i) => (
                  <label key={i} className="flex items-start gap-2 text-xs text-gray-700" data-testid={`gonogo-condicion-${i}`}>
                    <input type="checkbox" checked={condicionesAceptadas.includes(c)} onChange={() => toggleCondicion(c)} className="mt-0.5"/>
                    {c}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Motivo (obligatorio, mín. 10 caracteres)</label>
            <textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              data-testid="gonogo-motivo-input"
              placeholder="Ej: encaje técnico correcto, pero exigimos revisión jurídica del pliego antes de presentar..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white resize-y"
              rows={2}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={confirmDecision}
              disabled={saving || motivo.trim().length < 10}
              data-testid="gonogo-confirm-btn"
              className="text-xs font-semibold bg-[#007934] text-white px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1.5">
              {saving ? <Loader2 className="animate-spin" size={12}/> : <Check size={12}/>} Confirmar decisión
            </button>
            <button onClick={() => setPicking(null)} data-testid="gonogo-cancel-btn" className="text-xs text-gray-500 hover:text-gray-700">Cancelar</button>
          </div>
        </div>
      )}

      {decisions.length > 1 && (
        <details className="pt-1">
          <summary className="cursor-pointer text-[11px] font-semibold text-gray-500">Historial de decisiones ({decisions.length})</summary>
          <div className="mt-2 space-y-1.5">
            {decisions.slice(1).map(d => (
              <div key={d.id} className="text-[11px] p-2 border border-gray-100 rounded bg-gray-50" data-testid={`gonogo-history-${d.id}`}>
                <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold mr-2 ${DECISION_META[d.decision]?.cls || "bg-gray-400 text-white"}`}>
                  {DECISION_META[d.decision]?.label || d.decision}
                </span>
                <span className="text-gray-500">{d.decided_by} · {new Date(d.decided_at).toLocaleString("es-ES")}</span>
                <p className="text-gray-600 mt-0.5">{d.motivo}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function ViabilityResult({ data }) {
  const decisionCls = data.decision === "GO" ? "bg-green-500 text-white" :
                      data.decision === "NO_GO" ? "bg-rose-500 text-white" :
                      "bg-amber-500 text-white";
  const scores = data.scores || {};
  const scoreList = [
    { key: "encaje_tecnico",         label: "Encaje técnico" },
    { key: "viabilidad_economica",   label: "Viabilidad económica" },
    { key: "competitividad",         label: "Competitividad" },
    { key: "riesgo_operativo",       label: "Riesgo operativo (inverso)" },
    { key: "oportunidad_estrategica", label: "Oportunidad estratégica" },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4" data-testid="viability-result">
      {/* Header decision */}
      <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-4">
        <div>
          <div className={`inline-block rounded-lg px-3 py-1.5 text-sm font-bold ${decisionCls}`}>
            {data.decision}
          </div>
          <div className="text-xs text-gray-500 mt-2">Confidence: {data.confidence ?? "—"}%</div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-gray-900">{data.score_total ?? "—"}<span className="text-sm text-gray-400">/100</span></div>
          <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide">Score total</div>
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Resumen ejecutivo</h4>
        <p className="text-sm text-gray-800">{data.resumen_ejecutivo}</p>
      </div>

      {/* Scores bar */}
      <div className="space-y-2">
        {scoreList.map(s => (
          <div key={s.key}>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
              <span className="text-gray-700">{s.label}</span>
              <span className="font-mono text-gray-600">{scores[s.key] ?? 0}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div className="bg-[#007934] h-full" style={{ width: `${Math.min(100, Math.max(0, scores[s.key] || 0))}%` }}/>
            </div>
          </div>
        ))}
      </div>

      {/* Razones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
          <h4 className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1"><CheckCircle2 size={12}/> Razones GO</h4>
          <ul className="text-xs text-green-800 space-y-1 list-disc pl-4">
            {(data.razones_go || []).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
        <div className="bg-rose-50 rounded-lg p-3 border border-rose-100">
          <h4 className="text-xs font-bold text-rose-700 uppercase tracking-wide mb-2 flex items-center gap-1"><X size={12}/> Razones NO-GO / riesgos</h4>
          <ul className="text-xs text-rose-800 space-y-1 list-disc pl-4">
            {(data.razones_no_go || []).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      </div>

      {/* Condiciones */}
      {(data.condiciones || []).length > 0 && (
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
          <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1"><AlertTriangle size={12}/> Condiciones</h4>
          <ul className="text-xs text-amber-800 space-y-1 list-disc pl-4">
            {data.condiciones.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      {/* Riesgos */}
      {(data.riesgos_clave || []).length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Riesgos clave</h4>
          <div className="space-y-2">
            {data.riesgos_clave.map((r, i) => (
              <div key={i} className="text-xs border border-gray-100 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-800">{r.riesgo}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold
                    ${r.nivel === "alto" ? "bg-rose-100 text-rose-700" :
                      r.nivel === "medio" ? "bg-amber-100 text-amber-700" :
                      "bg-green-100 text-green-700"}`}>
                    {r.nivel}
                  </span>
                </div>
                <div className="text-gray-500 mt-1">Mitigación: {r.mitigacion}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Economics + competitors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-3">
        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">Economía estimada</h4>
          <div className="text-xs text-gray-700 space-y-1">
            <div>CAPEX: <span className="font-mono font-semibold">{fmtEur(data.estimacion_capex_eur)}</span></div>
            <div>OPEX anual: <span className="font-mono font-semibold">{fmtEur(data.estimacion_opex_anual_eur)}</span></div>
            <div>Estrategia precio: <span className="font-semibold">{data.estrategia_precio || "—"}</span></div>
          </div>
        </div>
        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">Competidores probables</h4>
          <div className="flex flex-wrap gap-1">
            {(data.competidores_probables || []).map((c, i) => (
              <span key={i} className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-semibold">{c}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-[#007934]">
        <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Recomendación al comité</h4>
        <p className="text-xs text-gray-900 font-medium">{data.recomendacion_final}</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// M9: Bid Writing Hub
// ═══════════════════════════════════════════════════════════════════════════
